import { Injectable } from '@angular/core';
import {
  Auth,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  User as FirebaseAuthUser,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateEmail,
  updatePassword,
} from '@angular/fire/auth';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Platform } from '@ionic/angular';
import { from, Observable, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private auth: Auth, private platform: Platform) {
    if (this.platform.is('android')) {
      SocialLogin.initialize({
        google: {
          webClientId: environment.socialLogin.google.webClientId,
        },
      });
    }
  }

  getAuthState(): Observable<FirebaseAuthUser | null> {
    return new Observable<FirebaseAuthUser | null>((observer) => {
      const unsubscribe = onAuthStateChanged(
        this.auth,
        (user) => {
          observer.next(user);
        },
        (error) => {
          observer.error(error);
        }
      );

      // Return the unsubscribe function to clean up
      return unsubscribe;
    });
  }

  loginWithEmailAndPassword(
    email: string,
    password: string
  ): Observable<FirebaseAuthUser> {
    return from(
      signInWithEmailAndPassword(this.auth, email, password).then((result) => {
        if (result && result.user) {
          return result.user;
        } else {
          throw new Error('Email/password login failed');
        }
      })
    );
  }

  createUserWithEmailAndPassword(
    email: string,
    password: string
  ): Observable<FirebaseAuthUser> {
    return from(
      createUserWithEmailAndPassword(this.auth, email, password).then(
        (result) => {
          if (result && result.user) {
            return result.user;
          } else {
            throw new Error('User creation failed');
          }
        }
      )
    );
  }

  googleLogin(): Observable<FirebaseAuthUser> {
    if (this.platform.is('android')) {
      return from(
        (async () => {
          const result: any = await SocialLogin.login({
            provider: 'google',
            options: {
              scopes: ['email', 'profile'],
            },
          });

          if (!result?.result?.idToken) {
            throw new Error('No ID token from Google login');
          }

          const credential = GoogleAuthProvider.credential(
            result.result.idToken
          );
          const userCredential = await signInWithCredential(
            this.auth,
            credential
          );

          if (!userCredential.user) {
            throw new Error('Firebase sign-in failed');
          }

          return userCredential.user;
        })()
      );
    } else {
      // fallback for web login
      return from(
        signInWithPopup(this.auth, new GoogleAuthProvider()).then((result) => {
          if (result && result.user) {
            return result.user;
          } else {
            throw new Error('Google login failed');
          }
        })
      );
    }
  }

  appleLogin(): Observable<FirebaseAuthUser> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    if (this.platform.is('cordova')) {
      // Use signInWithRedirect for mobile environments
      return from(
        signInWithRedirect(this.auth, provider).then(() => {
          return getRedirectResult(this.auth).then((result) => {
            if (result && result.user) {
              return result.user;
            } else {
              throw new Error('Apple login failed');
            }
          });
        })
      );
    } else {
      // Use signInWithPopup for web environments
      return from(
        signInWithPopup(this.auth, provider).then((result) => {
          if (result && result.user) {
            return result.user;
          } else {
            throw new Error('Apple login failed');
          }
        })
      );
    }
  }

  signOut(): Observable<void> {
    return from(signOut(this.auth));
  }

  sendPasswordResetEmail(email: string): Observable<void> {
    return from(sendPasswordResetEmail(this.auth, email));
  }

  // Helper to get current user or throw error
  private getCurrentUser(): Observable<FirebaseAuthUser> {
    return new Observable<FirebaseAuthUser>((observer) => {
      const user = this.auth.currentUser;
      if (user) {
        observer.next(user);
        observer.complete();
      } else {
        observer.error(new Error('No authenticated user found.'));
      }
    });
  }

  // Helper for re-authentication
  private reauthenticate(
    currentPassword?: string
  ): Observable<FirebaseAuthUser> {
    if (!currentPassword) {
      return throwError(
        () => new Error('Current password is required for re-authentication.')
      );
    }
    return this.getCurrentUser().pipe(
      switchMap((user) => {
        if (!user.email) {
          return throwError(
            () =>
              new Error('User email is not available for re-authentication.')
          );
        }
        const credential = EmailAuthProvider.credential(
          user.email,
          currentPassword
        );
        return from(reauthenticateWithCredential(user, credential)).pipe(
          switchMap(() => this.getCurrentUser()) // Return the user object after re-auth
        );
      })
    );
  }

  // New method for changing password
  changePassword(
    currentPassword?: string,
    newPassword?: string
  ): Observable<void> {
    if (!newPassword) {
      return throwError(() => new Error('New password cannot be empty.'));
    }
    return this.reauthenticate(currentPassword).pipe(
      switchMap((user) => from(updatePassword(user, newPassword)))
    );
  }

  // New method for changing email
  changeEmail(currentPassword?: string, newEmail?: string): Observable<void> {
    if (!newEmail) {
      return throwError(() => new Error('New email cannot be empty.'));
    }
    return this.reauthenticate(currentPassword).pipe(
      switchMap((user) => from(updateEmail(user, newEmail)))
    );
  }
}
